import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { Driver, NetworkLocation, TourPreview, TourStopSelection, Vehicle } from '../types';
import {
  buildTour, deliversHere as deliversHereRule, needsPickup as needsPickupRule, TourPoint
} from '../planiranje/sastav';
import { CENTRALNI, refOf } from '../planiranje/tokovi';
import { danTure, kratkiDatum } from '../planiranje/datum';
import { naslovPodesavanja, vozacPoslePromeneDana } from '../planiranje/podesavanja';

export type { TourPoint } from '../planiranje/sastav';

interface Props {
  stops: TourStopSelection[];
  /** Odakle tura krece i gde se vraca, punom oznakom — „MAG 021" ili „MPO 004". */
  base: string;
  onBaseChange: (base: string) => void;
  vehicles: Vehicle[];
  drivers: Driver[];
  locations: NetworkLocation[];
  /** Mesta ucitanog pravca. Odrediste na pravcu se isporucuje, ne pretovara. */
  routeDestinations?: string[];
  /**
   * Javlja redosled obilaska SA preuzimanjima, za mapu. Sastav ture je jedini
   * koji zna gde preuzimanje ulazi — mapa bez ovoga crta liniju mimo mesta na
   * kom vozilo stvarno staje.
   */
  onSequence?: (sequence: TourPoint[]) => void;
  fillThreshold: number;
  /**
   * Pun redosled ključeva iz App-a, sa „pretovar:" ključevima kad su već
   * dobili mesto. Sastav po njemu čuva pozicije preuzimanja.
   */
  orderedKeys: string[];
  /** Raste sa svakim učitavanjem pravca; sastav se tada sam složi. */
  orderSignal?: number;
  /** Postavlja novi redosled stanica, ključevima. */
  onReorder: (keys: string[]) => void;
  onRemove: (key: string) => void;
  onSaved: () => void;
}

/** „8h 25min" — brojka koja se čita izdaleka, bez reči oko sebe. */
function formatShort(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  return hours > 0 ? `${hours}h ${rest}min` : `${rest} min`;
}

/**
 * Slaganje ture od izabranih stanica.
 *
 * Popunjenost i vreme se računaju na zbir svih stanica, ne po jednom mestu.
 * Upozorenja ne blokiraju — operater sme svesno da nastavi, ali za nisku
 * popunjenost mora uneti razlog.
 */
export default function TourBuilder({
  stops, base, onBaseChange, vehicles, drivers, locations, routeDestinations = [], onSequence,
  fillThreshold, orderedKeys, orderSignal, onReorder, onRemove, onSaved
}: Props) {
  const [vehicleId, setVehicleId] = useState<number | null>(null);

  /**
   * Da li je operater sam dirao polazište.
   *
   * Dok nije, aplikacija sme da ga pomeri tamo gde je vozilo ostalo. Čim ga
   * dirne, ćuti — inače bi mu izbor menjala pod rukom.
   */
  const [baseTouched, setBaseTouched] = useState(false);

  /** Šta je aplikacija pomerila, da se na ekranu kaže i zašto. */
  const [parkedNote, setParkedNote] = useState<string | null>(null);

  /**
   * Kraj ture, kad nije isti kao polazište. Prazno = vraća se na polazište,
   * kao i do sada. Analiza scenarija (K18), 16.10.2026: kraj je uvek bio
   * polazište, pa je vozilo koje je prenoćilo u Kuli dobijalo turu Kula–Kula,
   * a pretovar se „smeštao" u prodavnicu umesto u magacin. CLAUDE.md: polazište
   * i odredište mogu biti bilo koji magacin ili MPO.
   */
  const [kraj, setKraj] = useState<string | null>(null);
  const [driverId, setDriverId] = useState<number | null>(null);

  /**
   * Tura za sutra — planiranje dan ranije je ravnopravno jutarnjem (CLAUDE.md).
   * Dotad je tura uvek bila za danas, pa se sutrašnja smena nije ni proveravala.
   */
  const [zaSutra, setZaSutra] = useState(false);
  const datum = danTure(zaSutra);

  /** Ko radi SUTRA. Prazno dok je tura za danas — tad važi spisak iz App-a. */
  const [vozaciSutra, setVozaciSutra] = useState<Driver[] | null>(null);
  const [name, setName] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [preview, setPreview] = useState<TourPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  /** Šta je poslednji predlog redosleda uštedeo, da operater vidi da je vredelo. */
  const [ordered, setOrdered] = useState<string | null>(null);
  const [ordering, setOrdering] = useState(false);

  /**
   * Odakle tura moze da krene. Magacini prvo, pa prodavnice.
   *
   * Prodavnice su tu zbog vanredne situacije: kad vozilo prenoci u Kuli jer
   * covek iz Kule vozi dok je magacinski na bolovanju, tura krece odande i
   * vraca se tamo. Redovno je i dalje centralni magacin, i on je prvi u listi.
   */
  const bases = useMemo(
    () => [
      ...locations.filter((l) => l.kind === 'Warehouse' && l.canBeRouted)
        .map((l) => ({ reference: `MAG ${l.code}`, name: l.name })),
      ...locations.filter((l) => l.kind === 'Store' && l.canBeRouted)
        .map((l) => ({ reference: `MPO ${l.code}`, name: l.name }))
    ],
    [locations]
  );

  /**
   * Za tok čija roba stoji van magacina, a odredište mu nije pokriveno robom
   * iz magacina: da li tura ipak PRODUŽAVA do odredišta, ili roba ide do
   * magacina pa drugom turom (druga noga). Ključ je tok, vrednost izbor.
   */
  const [deliverOverrides, setDeliverOverrides] = useState<Record<string, boolean>>({});

  /** Stanica čiji je tovar razgrnut u spisku. Jedna po jedna. */
  const [openStop, setOpenStop] = useState<string | null>(null);

  const needsPickup = (origin: string | null): boolean => needsPickupRule(origin, base);

  const deliversHere = (flowKey: string, origin: string | null, destination: string): boolean =>
    deliversHereRule(flowKey, origin, destination, stops, {
      base, routeDestinations, deliverOverrides, locations
    });

  /**
   * Tokovi čija roba ne stoji u polaznom magacinu — za svaki se vidi i bira
   * kuda ide. Ovo je operaterova komanda, ne izveštaj: dugmad menjaju nacrt.
   */
  const tokComande = useMemo(() => stops
    .filter((s): s is Extract<TourStopSelection, { kind: 'mpo' }> => s.kind === 'mpo')
    .filter((s) => needsPickup(s.group.origin))
    .map((s) => ({
      key: s.key,
      label: `${s.group.origin} → ${s.group.destination}`,
      quantity: s.group.items.reduce((sum, i) => sum + i.quantity, 0),
      delivers: deliversHere(s.key, s.group.origin, s.group.destination),
      zaKupce: s.group.zaKupce === true
    })),
    [stops, deliverOverrides, base]);


  // VOZILO SE NE BIRA SAMO. Do 15.10.2026. je ekran uzimao prvo (najveće)
  // vozilo i sklapao podešavanja — vidi planiranje/podesavanja.ts (analiza 2.0, P9).

  /**
   * Vozilo nije uvek prenoćilo u magacinu.
   *
   * Polazište se pomera tamo gde je vozilo ostalo posle poslednje ture — ali
   * samo dok ga operater nije sam dirao, i uvek uz rečenicu zašto. Ovo je
   * predlog: menja se bez ijednog objašnjenja.
   */
  useEffect(() => {
    if (baseTouched || vehicleId === null) return;

    const vozilo = vehicles.find((v) => v.id === vehicleId);
    const gde = vozilo?.currentLocation;

    if (!gde || gde === base) {
      setParkedNote(null);
      return;
    }

    onBaseChange(gde);
    setParkedNote(
      `${vozilo!.registration} je posle ture „${vozilo!.parkedAfterTour ?? '—'}" `
      + `ostalo u ${vozilo!.currentLocationName ?? gde}.`
    );
  }, [vehicleId, vehicles, base, baseTouched, onBaseChange]);

  // Kraj isti kao (novo) polazište nije poseban kraj.
  useEffect(() => {
    if (kraj !== null && kraj === base) setKraj(null);
  }, [base, kraj]);

  useEffect(() => {
    if (!zaSutra) {
      setVozaciSutra(null);
      return;
    }

    let ziv = true;
    api.drivers(datum)
      .then((d) => { if (ziv) setVozaciSutra(d); })
      // Bez spiska za sutra ostaje današnji; pregled ture ionako proverava smenu
      // za izabrani dan i kaže ako vozač tad ne radi.
      .catch(() => { if (ziv) setVozaciSutra(null); });

    return () => { ziv = false; };
  }, [zaSutra, datum]);

  const vozaci = vozaciSutra ?? drivers;

  // VOZAČ SE NE BIRA SAM: njemu po potvrdi ture odmah ode poruka, pa izbor
  // mora biti operaterov (analiza 2.0, P9). Promena dana: vozač koji tog dana
  // ne radi se skida, ne zamenjuje — podešavanja se tada sama otvore.
  useEffect(() => {
    const ostaje = vozacPoslePromeneDana(vozaci, driverId);
    if (ostaje !== driverId) setDriverId(ostaje);
  }, [vozaci]);

  // Uz nacrt idu i ključevi stanica, istim redom i sa istim izostavljanjima.
  // Bez toga se predloženi redosled ne bi mogao vratiti na spisak: indeksi bi
  // pokazivali na stanice koje u nacrt nisu ni ušle.
  /**
   * Sastav se računa i pre izbora vozila: spisak stanica i mapa moraju
   * pokazivati pravi obilazak dok se tura još slaže.
   */
  const composition = useMemo(
    () => buildTour(stops, {
      base, routeDestinations, deliverOverrides, locations, orderedKeys
    }),
    [stops, base, routeDestinations, deliverOverrides, locations, orderedKeys]);

  const draftKeys = composition.keys;
  const draftMerged = composition.merged;
  const sequence = composition.sequence;

  const draft = useMemo(() => {
    if (vehicleId === null || driverId === null || composition.draftStops.length === 0) {
      return null;
    }

    return {
      name: name.trim() || `Tura ${kratkiDatum(datum)}`,
      date: datum,
      vehicleId,
      driverId,
      startKind: refOf(base)?.kind ?? 1,
      startCode: refOf(base)?.code ?? '021',
      endKind: refOf(kraj ?? base)?.kind ?? 1,
      endCode: refOf(kraj ?? base)?.code ?? '021',
      stops: composition.draftStops,
      lowFillOverrideReason: overrideReason.trim() || null
    };
  }, [composition, vehicleId, driverId, name, overrideReason, base, kraj, datum]);

  // Otisak, da se roditelju ne javlja isti niz u krug.
  const sequenceKey = JSON.stringify(sequence);

  useEffect(() => {
    onSequence?.(sequence);
  }, [sequenceKey]);

  const refresh = useCallback(async () => {
    if (!draft) {
      setPreview(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      setPreview(await api.previewTour(draft));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }, [draft]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const lowFill = preview?.fill.canBeComputed === true
    && preview.fill.fillPercent < fillThreshold
    && !preview.fill.isOverloaded;
  const needsReason = lowFill && overrideReason.trim().length === 0;

  /**
   * Traži bolji redosled za stanice koje su već izabrane.
   *
   * Ne dodaje i ne izbacuje nijednu — samo ih poređa. Operater posle toga i
   * dalje pomera šta hoće strelicama; ovo je predlog, ne naredba.
   */
  const reorder = async () => {
    if (!draft) return;

    setOrdering(true);
    try {
      const result = await api.suggestOrder(draft);

      if (!result.changed || !result.order) {
        setOrdered('Redosled je već dobar — nema šta da se popravi.');
        return;
      }

      // Spojena stanica nosi više tokova: šire se na sve svoje ključeve, da
      // nijedan izbor ne nestane. Ključevi preuzimanja OSTAJU u redosledu:
      // složeno ume da preuzme u prolazu, mnogo pre isporuke koju roba čeka,
      // i ta pozicija se mora sačuvati — ne izvoditi ponovo po pravilu.
      onReorder(result.order
        .map((index) => draftKeys[index])
        .flatMap((key) => draftMerged.get(key) ?? [key]));

      setOrdered(
        `Složeno: ${result.beforeKm} → ${result.afterKm} km`
        + ` (${result.savedKm} km kraće)`
        + (result.measured ? '' : ', po vazdušnoj liniji — putevi nisu izmereni'));

      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOrdering(false);
    }
  };

  /**
   * Pravac se po učitavanju odmah i SLOŽI — istim postupkom kao dugme.
   *
   * Predlog pravca zna manje od slaganja: kupce i stanice ređa svojom,
   * prostijom logikom, a preuzimanja posle ulaze po pravilu, ne po
   * kilometrima. Bez ovoga je operater morao sam da klikne „Složi redosled"
   * posle svakog učitavanja — i dobijao dve različite ture za isti klik.
   */
  const handledOrderSignal = useRef(0);
  useEffect(() => {
    if (!orderSignal || orderSignal === handledOrderSignal.current) return;
    if (!draft || ordering) return;

    handledOrderSignal.current = orderSignal;
    void reorder();
  }, [orderSignal, draft]);

  /** Redosled složenih stanica nazad u ključeve izbora. */
  const expandComposed = (orderedComposed: string[]) =>
    orderedComposed.flatMap((key) => draftMerged.get(key) ?? [key]);

  const moveRow = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draftKeys.length) return;

    const next = [...draftKeys];
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(expandComposed(next));
  };

  /** Skida CELU stanicu: spojena nosi više tokova i svi silaze zajedno. */
  const removeRow = (key: string) => {
    for (const flowKey of draftMerged.get(key) ?? [key]) {
      onRemove(flowKey);
    }
  };

  /** Tovar stanice po nalozima — šta se tu tačno tovari ili ostavlja. */
  const cargoOf = (items: { articleName: string; quantity: number; erpDocument: string | null }[]) => {
    const map = new Map<string, {
      document: string | null; pieces: number; items: { articleName: string; quantity: number }[];
    }>();

    for (const item of items) {
      const key = item.erpDocument ?? '';
      let entry = map.get(key);
      if (!entry) {
        entry = { document: item.erpDocument, pieces: 0, items: [] };
        map.set(key, entry);
      }
      entry.pieces += item.quantity;
      entry.items.push({ articleName: item.articleName, quantity: item.quantity });
    }

    return Array.from(map.values());
  };

  /** „MPO 004" → „Kula": kuda pretovarna roba dalje ide, rečima. */
  const placeName = (reference: string): string => {
    const match = reference.match(/^(MPO|MAG) (\d{3})$/);
    if (!match) return reference;

    const location = locations.find((l) =>
      l.kind === (match[1] === 'MAG' ? 'Warehouse' : 'Store') && l.code === match[2]);
    return location ? (location.city || location.name) : reference;
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.saveTour(draft);
      setSaved(`Tura „${result.name}" sačuvana${zaSutra ? ` za sutra, ${kratkiDatum(datum)}` : ''} — `
        + `${result.stops} stanica. Roba je rezervisana.`);
      setOverrideReason('');
      setName('');
      // Sledeća tura počinje od početka: polazište opet sme da predloži vozilo,
      // a vozilo i vozač se biraju iznova (analiza 2.0, P11 i P9).
      setBaseTouched(false);
      setParkedNote(null);
      setKraj(null);
      setVehicleId(null);
      setDriverId(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Boja brojke popunjenosti: preko kapaciteta, ispod praga, ili u redu. */
  const overloadedEnRoute = preview !== null
    && preview.fill.canBeComputed
    && preview.peakM3 > preview.fill.vehicleCapacityM3;

  const fillTone = preview?.fill.isOverloaded || overloadedEnRoute
    ? 'figure--bad'
    : lowFill ? 'figure--warn' : preview?.fill.canBeComputed ? 'figure--ok' : '';

  if (stops.length === 0) {
    return (
      <p className="empty">
        Izaberi odredišta i isporuke sa leve strane — mogu se mešati u istoj turi.
        Redosled im daješ ovde.
      </p>
    );
  }

  return (
    <div className="builder">
      {/* Traka odluke. Popunjenost, trajanje i „Sačuvaj" stoje NA VRHU i lepe se
          uz njega dok se skroluje, jer se po ta dva broja odlučuje na svaki
          klik po levim listama. Ranije su bili na dnu kolone od četrnaest
          elemenata, ispod četiri padajuće liste koje se postave jednom. */}
      <div className="decide">
        <div className="decide__figures">
          <span className={`figure ${fillTone}`}>
            <span className="figure__label">Popunjenost</span>
            <span className="figure__value">
              {preview?.fill.canBeComputed ? `${preview.fill.fillPercent}%` : '—'}
            </span>
            <span className="figure__sub">
              {preview?.fill.canBeComputed
                ? preview.peakM3 > preview.fill.loadedVolumeM3
                  ? `polazak ${preview.fill.loadedVolumeM3} · špic ${preview.peakM3} od ${preview.fill.vehicleCapacityM3} m³`
                  : `${preview.fill.loadedVolumeM3} od ${preview.fill.vehicleCapacityM3} m³`
                : 'zapremina nije poznata'}
            </span>
          </span>

          <span className={`figure ${preview && !preview.timing.fitsInShift ? 'figure--warn' : ''}`}>
            <span className="figure__label">Trajanje</span>
            <span className="figure__value">{preview ? formatShort(preview.timing.totalMinutes) : '—'}</span>
            <span className="figure__sub">
              {preview
                ? (preview.timing.fitsInShift
                    ? `staje u smenu · ${preview.timing.distanceKm} km`
                    : `preko smene · ${preview.timing.distanceKm} km`)
                : 'računa se'}
            </span>
          </span>
        </div>

        <div className="decide__act">
          <button
            type="button"
            className="primary"
            onClick={() => void save()}
            disabled={busy || !draft || preview?.canBeSaved === false || needsReason}
          >
            {busy ? 'Čuvam…' : 'Sačuvaj turu'}
          </button>
          {busy && <span className="muted">Računam…</span>}
        </div>
      </div>

      {error && <p className="warning">{error}</p>}
      {saved && <div className="health health--ok">{saved}</div>}

      {preview && !busy && (
        <>
          {preview.blockers.map((b) => (
            <p key={b} className="blocker">{b}</p>
          ))}
          {preview.warnings.map((w) => (
            <p key={w} className="warning">{w}</p>
          ))}
          {(preview.notices ?? []).map((n) => (
            <p key={n} className="note">{n}</p>
          ))}
        </>
      )}

      {tokComande.length > 0 && (
        <div className="pickups">
          <h3 className="pickups__title">Roba van magacina na ovoj turi</h3>
          <p className="note">
            Uzima se u prolazu. Ako odredište nije pokriveno robom iz magacina,
            biraš: produžiti turu do odredišta, ili ostaviti u magacinu pa
            drugom turom.
          </p>

          {tokComande.map((tok) => (
            <div key={tok.key} className="pickups__row">
              <span>
                {tok.label}
                <span className="muted"> · {tok.quantity} kom</span>
              </span>
              <span className="pickups__mode">
                {/* Roba za kupca nema izbor: kupac je čeka u objektu. */}
                {tok.zaKupce ? (
                  <span className="flag">za kupca — ide pravo u objekat</span>
                ) : (
                  <>
                    <button
                      type="button"
                      className={tok.delivers ? 'primary' : ''}
                      onClick={() => setDeliverOverrides((c) => ({ ...c, [tok.key]: true }))}
                    >
                      vozi do odredišta
                    </button>
                    <button
                      type="button"
                      className={tok.delivers ? '' : 'primary'}
                      onClick={() => setDeliverOverrides((c) => ({ ...c, [tok.key]: false }))}
                    >
                      u magacin, druga noga
                    </button>
                  </>
                )}
                <button type="button" onClick={() => onRemove(tok.key)} title="Skini tok sa ture">
                  ×
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {preview !== null && preview.loadProfile.length > 1 && (
        <details className="cargo">
          <summary>
            Tovar po stanicama
            {preview.peakAfter
              ? ` — najpunije posle: ${preview.peakAfter}`
              : ' — najpunije na polasku'}
          </summary>
          <table className="cargo__table">
            <thead>
              <tr>
                <th>Stanica</th>
                <th>uzima</th>
                <th>ostavlja</th>
                <th>u vozilu</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="muted">polazak</td>
                <td /><td />
                <td>{preview.fill.loadedVolumeM3} m³</td>
              </tr>
              {preview.loadProfile.map((row, index) => (
                <tr
                  key={`${row.label}-${index}`}
                  className={
                    preview.fill.canBeComputed && row.afterM3 > preview.fill.vehicleCapacityM3
                      ? 'cargo__row--over'
                      : row.afterM3 === preview.peakM3 && preview.peakM3 > 0 ? 'cargo__row--peak' : ''
                  }
                >
                  <td>{row.label}{row.hasUnknown ? ' *' : ''}</td>
                  <td>{row.inM3 > 0 ? `+${row.inM3}` : ''}</td>
                  <td>{row.outM3 > 0 ? `−${row.outM3}` : ''}</td>
                  <td>{row.afterM3} m³</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="note">
            * stavka bez poznate zapremine — nije u brojkama, tovar je stvarno veći.
          </p>
        </details>
      )}

      {/* Polje stoji dok god je tura ispod praga — uslov NE sme biti
          needsReason: on se gasi prvim ukucanim slovom, pa je polje nestajalo
          operateru ispod prstiju. */}
      {lowFill && (
        <div className="field">
          <label htmlFor="reason">Razlog za puštanje ispod praga</label>
          <input
            id="reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="npr. Odžaci čekaju tri dana"
          />
          <p className="note">Razlog se beleži uz turu, da se kasnije vidi zašto je puštena.</p>
        </div>
      )}

      {/* Spisak je ITINERER: redovi su mesta na kojima vozilo staje, onim
          redom kojim se voze — sa preuzimanjima. Tokovi ka istom mestu su
          jedna stanica; roba koja ide u magacin nema svoju stanicu, nju nosi
          red preuzimanja („vozi ka: magacin"). */}
      <ol className="stops">
        {draftKeys.map((key, index) => {
          const draftStop = composition.draftStops[index];
          const point = sequence[index];
          const isPretovar = key.startsWith('pretovar:');
          const mergedFlows = draftMerged.get(key);
          const pieces = draftStop.items.reduce((sum, i) => sum + i.quantity, 0);

          const kupac = draftStop.kind === 1
            ? stops.find((s): s is Extract<TourStopSelection, { kind: 'kupac' }> =>
              s.kind === 'kupac' && s.key === key)
            : undefined;
          const b2c = key.startsWith('preuzimanje:')
            ? stops.find((s): s is Extract<TourStopSelection, { kind: 'preuzimanje' }> =>
              s.kind === 'preuzimanje' && s.key === key)
            : undefined;
          const pickup = composition.pickupSummary.get(key);

          return (
            <li
              key={key}
              className={`stop${kupac ? ' stop--kupac' : ''}`
                + `${b2c || isPretovar ? ' stop--preuzimanje' : ''}`}
            >
              <span className="stop__index">{index + 1}</span>
              {/* Klik na naziv razgrće tovar stanice — koji nalozi, koja
                  roba — da se ne traži peške po levoj koloni. */}
              <button
                type="button"
                className="stop__toggle"
                aria-expanded={openStop === key}
                onClick={() => setOpenStop(openStop === key ? null : key)}
              >
              <span className="stop__name">
                {isPretovar ? `Preuzimanje: ${point.label}` : point.label}
                <span className="stop__meta">
                  {kupac && (
                    <>
                      kupac · {pieces} kom
                      {kupac.delivery.requiresTwoPeople && <strong> · dva čoveka</strong>}
                    </>
                  )}
                  {b2c && (
                    <>
                      <strong>uzima se</strong> · {pieces} kom za{' '}
                      {b2c.stop.pickupFor.length} isporuk
                      {b2c.stop.pickupFor.length === 1 ? 'u' : 'e'}
                    </>
                  )}
                  {isPretovar && pickup && (
                    <>
                      <strong>uzima se</strong> · {pickup.pieces} kom · vozi ka:{' '}
                      {Array.from(new Set([
                        ...pickup.feeds.map(placeName),
                        ...(pickup.toWarehouse ? ['magacin'] : [])
                      ])).join(', ')}
                    </>
                  )}
                  {!kupac && !b2c && !isPretovar && (
                    <>
                      {draftStop.items.length} stavki · {pieces} kom
                      {mergedFlows && mergedFlows.length > 1 && ` · ${mergedFlows.length} toka`}
                    </>
                  )}
                </span>
              </span>
              </button>
              <span className="stop__actions">
                <button type="button" onClick={() => moveRow(index, -1)} disabled={index === 0} title="Pomeri gore">↑</button>
                <button type="button" onClick={() => moveRow(index, 1)} disabled={index === draftKeys.length - 1} title="Pomeri dole">↓</button>
                {/* Preuzimanje se ne skida samo — nestaje kad se skinu tokovi
                    čiju robu nosi. */}
                {!isPretovar && (
                  <button type="button" onClick={() => removeRow(key)} title="Izbaci iz ture">×</button>
                )}
              </span>
              {openStop === key && (
                <div className="stop__cargo">
                  {cargoOf(draftStop.items).map((entry) => (
                    <div key={entry.document ?? 'bez-naloga'} className="stop__cargo-row">
                      <span>
                        {draftStop.kind === 1 ? 'Otpremnica' : 'Nalog'}{' '}
                        {entry.document ?? '—'}
                        <span className="muted"> · {entry.pieces} kom</span>
                      </span>
                      <span className="muted">
                        {entry.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Slaganje stoji uz spisak jer nad njim i radi. */}
      {stops.length > 2 && (
        <div className="builder__order">
          <button type="button" disabled={ordering || !draft} onClick={() => void reorder()}>
            {ordering ? 'Slažem…' : 'Složi redosled'}
          </button>
          {ordered && <span className="muted">{ordered}</span>}
        </div>
      )}

      {/* Podešavanja se postave jednom po turi, pa stoje sklopljena. Otvaraju se
          sama dok vozilo ili vozač nisu izabrani — tada su posao, ne podatak.
          Naslov kaže šta je izabrano i kad su sklopljena: tura ne sme da ode na
          vozača koga niko nije video (analiza 2.0, P9). */}
      <details className="details" open={vehicleId === null || driverId === null}>
        <summary>
          {naslovPodesavanja({
            danOpis: `${zaSutra ? 'sutra' : 'danas'}, ${kratkiDatum(datum)}`,
            vozilo: vehicles.find((v) => v.id === vehicleId),
            vozac: vozaci.find((d) => d.id === driverId)
          })}
        </summary>

        <div className="field">
          <label htmlFor="dan">Dan ture</label>
          <select id="dan" value={zaSutra ? 'sutra' : 'danas'} onChange={(e) => setZaSutra(e.target.value === 'sutra')}>
            <option value="danas">Danas, {kratkiDatum(danTure(false))}</option>
            <option value="sutra">Sutra, {kratkiDatum(danTure(true))}</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="name">Naziv ture</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Zapadna tura" />
        </div>

        <div className="field">
          <label htmlFor="base">Polazište</label>
          <select
            id="base"
            value={base}
            onChange={(e) => { setBaseTouched(true); setParkedNote(null); onBaseChange(e.target.value); }}
          >
            {bases.map((b) => (
              <option key={b.reference} value={b.reference}>{b.name}</option>
            ))}
          </select>

          {/* Kad je polazište pomerila aplikacija, mora da se vidi ko je i
              zašto — inače izgleda kao da je operater negde promašio. */}
          {parkedNote && (
            <p className="note">
              {parkedNote} Polazište je pomereno tamo. <strong>Promeni slobodno</strong> ako
              znaš da je vozilo negde drugde.
            </p>
          )}

          {/* Vanredno polazište menja šta se tovari na polasku, pa se kaže šta
              se promenilo — inače operater vidi drugi sastav i ne zna zašto. */}
          {base !== CENTRALNI && (
            <p className="note">
              Tura kreće {kraj === null ? 'i vraća se ' : ''}ovde. Na polasku se tovari samo
              roba koja već stoji na tom mestu; po ostalo se svraća u centralni magacin.
            </p>
          )}
        </div>

        {/* KRAJ TURE (K18). Podrazumevano isti kao polazište, kao i do sada. */}
        <div className="field">
          <label htmlFor="kraj">Kraj ture</label>
          <select
            id="kraj"
            value={kraj ?? ''}
            onChange={(e) => setKraj(e.target.value === '' || e.target.value === base ? null : e.target.value)}
          >
            <option value="">isto kao polazište</option>
            {bases.filter((b) => b.reference !== base).map((b) => (
              <option key={b.reference} value={b.reference}>{b.name}</option>
            ))}
          </select>
          {kraj !== null && (
            <p className="note">
              Vozilo se posle ture vraća u {bases.find((b) => b.reference === kraj)?.name ?? kraj}
              {' '}— tu se računa povratak, tu ostaje vozilo, i tu se smešta pretovar.
            </p>
          )}
          {kraj === null && parkedNote && base !== CENTRALNI && (
            <p className="note">
              Ako se vozilo posle ture vraća u centralni magacin, izaberi ga kao kraj ture.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="vehicle">Vozilo</label>
          <select
            id="vehicle"
            value={vehicleId ?? ''}
            onChange={(e) => setVehicleId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">— izaberi vozilo —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.registration} — {v.capacityM3.toFixed(2)} m³{!v.capacityIsMeasured ? ' (privremeno)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="driver">Vozač</label>
          <select
            id="driver"
            value={driverId ?? ''}
            onChange={(e) => setDriverId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">— izaberi vozača —</option>
            {vozaci.map((d) => (
              <option key={d.id} value={d.id} disabled={!d.isAvailable}>
                {d.fullName}{d.isAvailable ? ` — ${d.shiftStart}–${d.shiftEnd}` : ' — ne radi'}
              </option>
            ))}
          </select>
        </div>
      </details>

      {preview && !busy && (
        <>
          {/* Rečenice ostaju — one objašnjavaju broj iznad. Broj se skenira,
              rečenica se čita kad nešto ne valja. */}
          <div className="estimate">
            <div className={`gauge ${preview.fill.isOverloaded ? 'gauge--over' : lowFill ? 'gauge--low' : 'gauge--ok'}`}>
              <div className="gauge__fill" style={{ width: `${Math.min(100, preview.fill.fillPercent)}%` }} />
            </div>
            <p className="explanation">{preview.fill.explanation}</p>
            <p className={`explanation ${preview.timing.fitsInShift ? '' : 'explanation--warn'}`}>
              {preview.timing.explanation}
            </p>

            {/* Po čemu je procena računata. Bez ovoga broj izgleda jednako
                pouzdan i kad dolazi iz merenja i kad iz podrazumevane
                vrednosti — a razlika je bila dva do tri puta. */}
            {preview.estimateBasis && (
              <p className="muted estimate__basis">
                {preview.estimateFromMeasurements ? '📏 ' : ''}{preview.estimateBasis}
              </p>
            )}
          </div>

          {preview.timing.legs.length > 0 && (
            <details className="details">
              <summary>Deonice — {preview.timing.distanceKm} km ukupno</summary>
              <ul className="legs">
                {preview.timing.legs.map((leg, i) => (
                  <li key={i}>
                    {leg.from} → {leg.to}
                    <span className="muted"> {leg.distanceKm} km · {leg.driveMinutes} min</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.loadingOrder.length > 0 && (
            <details className="details" open>
              <summary>Redosled utovara</summary>
              <ol className="legs">
                {preview.loadingOrder.map((p) => (
                  <li key={p.loadingSequence}>
                    {p.description}
                    {p.isNearestDoor && <strong> — do vrata, prvo se isporučuje</strong>}
                  </li>
                ))}
              </ol>
            </details>
          )}

        </>
      )}
    </div>
  );
}
